import { useMemo } from 'react';
import { useGetAiWorkerConfigurationsQuery, useTypedSelector } from '@circuitry-ai/doc-data';
import {
    buildAgentConfigurationFallbackPayload,
    buildAgentQueryPayload,
    extractConfigurationsFromResponse,
} from './agentConfigurationShared';

type UseAgentConfigurationQueryArgs = {
    aiworkerId: string;
    configId?: string;
    configVersion?: number | string;
    configVersionId?: string;
    canUpdate: boolean;
};

export function useAgentConfigurationQuery({
    aiworkerId,
    configId,
    configVersion,
    configVersionId,
    canUpdate,
}: UseAgentConfigurationQueryArgs) {
    const token = useTypedSelector((state) => state.auth.token);

    const queryPayload = useMemo(
        () => buildAgentQueryPayload(aiworkerId, configId, configVersion, configVersionId),
        [aiworkerId, configId, configVersion, configVersionId]
    );

    const fallbackQueryPayload = useMemo(
        () => buildAgentConfigurationFallbackPayload(aiworkerId),
        [aiworkerId]
    );

    const {
        data: activeConfigurationData,
        isLoading: isLoadingActive,
        isFetching: isFetchingActive,
        isError: isErrorActive,
        refetch: refetchActive,
    } = useGetAiWorkerConfigurationsQuery(queryPayload, {
        skip: !token || !aiworkerId,
        refetchOnMountOrArgChange: true,
    });

    const shouldFetchFallback = Boolean(
        token && aiworkerId && !isLoadingActive && !(activeConfigurationData?.configurations?.length)
    );

    const {
        data: fallbackConfigurationData,
        isLoading: isLoadingFallback,
        isFetching: isFetchingFallback,
        isError: isErrorFallback,
        refetch: refetchFallback,
    } = useGetAiWorkerConfigurationsQuery(fallbackQueryPayload, {
        skip: !shouldFetchFallback,
        refetchOnMountOrArgChange: true,
    });

    const configurationData = activeConfigurationData?.configurations?.length
        ? activeConfigurationData
        : fallbackConfigurationData;
    const isLoading = isLoadingActive || (shouldFetchFallback && isLoadingFallback);
    const isFetching = isFetchingActive || (shouldFetchFallback && isFetchingFallback);
    const isError = isErrorActive || isErrorFallback;

    const refetch = async () => {
        await refetchActive();
        if (shouldFetchFallback) {
            await refetchFallback();
        }
    };

    const configurations = extractConfigurationsFromResponse(configurationData);
    const selectedConfiguration = configurations[0] as Record<string, unknown> | undefined;
    const isInactiveConfiguration =
        selectedConfiguration?.status === 'inactive' || selectedConfiguration?.is_active === false;
    const isConfigurationEditable = canUpdate && !isInactiveConfiguration;
    const selectedConfigurationSignature = useMemo(
        () => JSON.stringify(selectedConfiguration ?? null),
        [selectedConfiguration]
    );

    return {
        selectedConfiguration,
        selectedConfigurationSignature,
        isLoading,
        isFetching,
        isError,
        isConfigurationEditable,
        refetch,
    };
}
